import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, MapPin, Edit, Trash2, Building, Users, Crown } from 'lucide-react';
import { useLicence, canAccess } from '../contexts/LicenceContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocationsPage() {
  const { t } = useTranslation();
  const licence = useLicence();
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '' });
  const [tierBlocked, setTierBlocked] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(data);
      setTierBlocked(false);
    } catch (err) {
      if (err.response?.status === 403) setTierBlocked(true);
    }
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff`, { withCredentials: true });
      setStaff(data);
    } catch {}
  }, []);

  useEffect(() => { fetchLocations(); fetchStaff(); }, [fetchLocations, fetchStaff]);

  const openNew = () => {
    setEditLoc(null);
    setForm({ name: '', address: '', phone: '', email: '' });
    setDialogOpen(true);
  };

  const openEdit = (loc) => {
    setEditLoc(loc);
    setForm({ name: loc.name, address: loc.address || '', phone: loc.phone || '', email: loc.email || '' });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editLoc) {
        await axios.put(`${API}/locations/${editLoc.id}`, form, { withCredentials: true });
        toast.success('Location updated');
      } else {
        await axios.post(`${API}/locations`, form, { withCredentials: true });
        toast.success('Location added');
      }
      setDialogOpen(false);
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      await axios.delete(`${API}/locations/${id}`, { withCredentials: true });
      toast.success('Location deleted');
      fetchLocations();
    } catch { toast.error('Failed to delete'); }
  };

  const getStaffForLocation = (locId) => staff.filter(s => s.location_id === locId);

  const canAddMore = !licence || canAccess(licence, 'multi_location') || locations.length === 0;

  return (
    <div className="fade-in space-y-6" data-testid="locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>Locations</h1>
          <p className="text-sm text-[#5C6656] mt-1">Manage your business locations</p>
        </div>
        {canAddMore ? (
          <Button onClick={openNew} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="add-location-btn">
            <Plus size={16} className="mr-1" /> Add Location
          </Button>
        ) : (
          <div className="flex items-center gap-2 text-xs text-[#D4A373] bg-[#D4A373]/10 px-4 py-2 rounded-lg" data-testid="upgrade-locations-msg">
            <Crown size={14} />
            <span>Upgrade to <strong>Business</strong> or <strong>Premium</strong> to add more locations</span>
          </div>
        )}
      </div>

      {tierBlocked && (
        <Card className="border-[#D4A373]">
          <CardContent className="py-8 text-center">
            <Building size={40} className="mx-auto text-[#D4A373] mb-4" />
            <h3 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Multi-Location Requires Business Plan</h3>
            <p className="text-sm text-[#5C6656] mt-2 max-w-md mx-auto">
              Upgrade to Apptio Business (£29.99/month) to manage multiple locations under one account.
            </p>
          </CardContent>
        </Card>
      )}

      {!tierBlocked && locations.length === 0 && (
        <Card className="border-[#E5E7E1]">
          <CardContent className="py-12 text-center">
            <MapPin size={40} className="mx-auto text-[#E5E7E1] mb-4" />
            <p className="text-[#5C6656]">No locations yet. Add your first location to get started.</p>
            <p className="text-xs text-[#5C6656] mt-2">Tip: Create locations, then assign staff to each location from the Staff page.</p>
          </CardContent>
        </Card>
      )}

      {!tierBlocked && locations.length > 0 && (
        <Card className="border-[#E5E7E1] shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E7E1]">
                  <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">Location</TableHead>
                  <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">Address</TableHead>
                  <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">Contact</TableHead>
                  <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">Staff</TableHead>
                  <TableHead className="text-xs font-bold tracking-wider uppercase text-[#5C6656] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map(loc => {
                  const locStaff = getStaffForLocation(loc.id);
                  return (
                    <TableRow key={loc.id} data-testid={`location-row-${loc.id}`} className="border-[#E5E7E1] hover:bg-[#F9F8F6]">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-[#E9EDDF] flex items-center justify-center">
                            <MapPin size={14} className="text-[#556B2F]" />
                          </div>
                          <span className="font-medium text-[#1A2421]">{loc.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-[#5C6656]">{loc.address || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5C6656]">{loc.phone || loc.email || '-'}</TableCell>
                      <TableCell>
                        <Badge className="bg-[#E9EDDF] text-[#556B2F] text-xs">
                          <Users size={10} className="mr-1" /> {locStaff.length} staff
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(loc)} className="text-[#556B2F]"><Edit size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(loc.id)} className="text-[#E2725B]"><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md border-[#E5E7E1]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editLoc ? 'Edit Location' : 'Add Location'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="space-y-2"><Label>Location Name *</Label><Input data-testid="location-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="border-[#E5E7E1]" /></div>
            <div className="space-y-2"><Label>Address</Label><Input data-testid="location-address" value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} className="border-[#E5E7E1]" placeholder="123 High Street, London" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input data-testid="location-phone" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="border-[#E5E7E1]" /></div>
            <div className="space-y-2"><Label>Email</Label><Input data-testid="location-email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="border-[#E5E7E1]" /></div>
            <Button data-testid="save-location-btn" type="submit" className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
              {editLoc ? 'Update Location' : 'Add Location'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
